import React, { useMemo } from 'react';
import type { StaffMember, Shift, Schedule, Department } from '../../types';
import Modal from '../ui/Modal';

interface DailyShiftDetailsModalProps {
    date: string;
    currentStaffMember: StaffMember;
    allStaff: StaffMember[];
    schedule: Schedule;
    shifts: Shift[];
    departments: Department[];
    onClose: () => void;
}

const getInitials = (name: string) => {
    if (!name) return '';
    const names = name.split(' ');
    if (names.length > 1 && names[names.length - 1]) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const toMinutes = (timeString: string): number => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
};

// FIX: Explicitly type StaffListItem as a React.FC to resolve errors with the 'key' prop.
const StaffListItem: React.FC<{ item: { staff: StaffMember, shift: Shift | null, department: Department | null } }> = ({ item }) => (
    <li className="flex items-center gap-3 py-2">
        <div
            className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-xs"
            style={{ backgroundColor: item.staff.color }}
        >
            {getInitials(item.staff.name)}
        </div>
        <div className="flex-grow">
            <p className="font-semibold text-gray-800 dark:text-gray-200">{item.staff.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
                {item.department ? item.department.name : 'No Department'}
            </p>
        </div>
        {item.shift && (
            <div className="text-right text-xs">
                <p className="font-medium text-gray-700 dark:text-gray-300">{item.shift.name}</p>
                <p className="text-gray-500 dark:text-gray-400">{item.shift.startTime} - {item.shift.endTime}</p>
            </div>
        )}
    </li>
);


const DailyShiftDetailsModal: React.FC<DailyShiftDetailsModalProps> = ({ date, currentStaffMember, allStaff, schedule, shifts, departments, onClose }) => {
    
    const shiftsMap = useMemo(() => new Map<string, Shift>(shifts.map(s => [s.id, s])), [shifts]);
    const departmentsMap = useMemo(() => new Map<string, Department>(departments.map(d => [d.id, d])), [departments]);

    const formattedDate = new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

    const myEntry = schedule[currentStaffMember.id]?.[date];
    const myShift = myEntry?.shiftId ? shiftsMap.get(myEntry.shiftId) : null;
    const myShiftEndTime = myShift ? toMinutes(myShift.endTime) : -1;

    const colleaguesOnShift = useMemo(() => {
        return allStaff
            .filter(staff => staff.id !== currentStaffMember.id && schedule[staff.id]?.[date]?.status === 'ASSIGNED')
            .map(staff => {
                const entry = schedule[staff.id][date];
                const shift = entry.shiftId ? shiftsMap.get(entry.shiftId) : null;
                const department = staff.departmentId ? departmentsMap.get(staff.departmentId) : null;
                return { staff, shift, department };
            })
            .filter(item => !!item.shift);
    }, [allStaff, schedule, date, currentStaffMember.id, shiftsMap, departmentsMap]);

    const reliefTeam = useMemo(() => {
        if (!myShift) return [];
        return colleaguesOnShift
            .filter(colleague => 
                colleague.staff.departmentId === currentStaffMember.departmentId &&
                colleague.shift && toMinutes(colleague.shift.startTime) >= myShiftEndTime
            )
            .sort((a, b) => toMinutes(a.shift!.startTime) - toMinutes(b.shift!.startTime));
    }, [myShift, colleaguesOnShift, currentStaffMember.departmentId, myShiftEndTime]);

    return (
        <Modal title={`Shift Details for ${formattedDate}`} onClose={onClose}>
            <div className="p-6 space-y-6">
                <div>
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Your Shift</h3>
                    {myShift ? (
                        <div className="p-3 rounded-lg text-white" style={{ backgroundColor: myShift.color }}>
                            <p className="font-bold text-lg">{myShift.name}</p>
                            <p className="text-sm opacity-90">{myShift.startTime} - {myShift.endTime}</p>
                        </div>
                    ) : (
                        <p className="p-3 bg-gray-100 dark:bg-slate-700 rounded-lg text-sm text-gray-600 dark:text-gray-300">You are not scheduled for a shift on this day.</p>
                    )}
                </div>

                <div>
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Working With You</h3>
                    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                        {colleaguesOnShift.length > 0 ? (
                            <ul className="divide-y divide-gray-200 dark:divide-slate-600">
                                {colleaguesOnShift.map(item => <StaffListItem key={item.staff.id} item={item} />)}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No other staff members are scheduled today.</p>
                        )}
                    </div>
                </div>

                {myShift && (
                     <div>
                        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Relief Team (Your Department)</h3>
                        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                            {reliefTeam.length > 0 ? (
                                <ul className="divide-y divide-gray-200 dark:divide-slate-600">
                                    {reliefTeam.map(item => <StaffListItem key={item.staff.id} item={item} />)}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No relief staff from your department are scheduled after your shift.</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
             <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 flex justify-end border-t dark:border-slate-700">
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default DailyShiftDetailsModal;